import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getExpatEnPageBySlug } from '../../content/expatEnPages';
import usePageMeta from '../../hooks/usePageMeta';
import { PHONE_TEL_URL, PHONE_DISPLAY, WHATSAPP_URL, GOOGLE_MAPS_URL } from '../../constants';

// Standalone English page for the expat-targeted funnel. Unlike the rest of
// the site (which shares one URL per page and toggles AR/EN client-side),
// these pages need their own real URL with hreflang + a self-referencing
// canonical, per the client's plan for reaching English-language searches.
// Content is authored in English directly rather than pulled through
// react-i18next, and the document's lang/dir is forced to en/ltr for the
// lifetime of the page regardless of the site-wide language toggle.
function ExpatEnPage() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const page = getExpatEnPageBySlug(slug);

  usePageMeta(
    page
      ? {
          title: page.seoTitle,
          description: page.description,
          keywords: page.keywords,
          path: page.path,
          alternates: [
            { hreflang: 'en', path: page.path },
            { hreflang: 'ar', path: page.arPath },
          ],
        }
      : {}
  );

  useEffect(() => {
    const previousLang = document.documentElement.lang;
    const previousDir = document.documentElement.dir;
    document.documentElement.lang = 'en';
    document.documentElement.dir = 'ltr';
    return () => {
      document.documentElement.lang = i18n.language === 'ar' ? 'ar' : previousLang;
      document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : previousDir;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!page) {
    return <Navigate to="/practice-areas" replace />;
  }

  const relatedPages = Array.isArray(page.related)
    ? page.related.map((s) => getExpatEnPageBySlug(s)).filter(Boolean)
    : [];

  return (
    <article className="py-5 py-lg-6 bg-white" lang="en" dir="ltr">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
              <Link to="/practice-areas" className="link-navy text-decoration-none small d-inline-block">
                &larr; All services
              </Link>
              <Link to={page.arPath} className="link-navy text-decoration-none small d-inline-block">
                القراءة بالعربية
              </Link>
            </div>

            <h1 className="h2 fw-semibold text-navy mb-3">{page.h1}</h1>
            <p className="lead text-muted mb-4">{page.description}</p>
            <p className="mb-4">{page.intro}</p>

            {Array.isArray(page.body) &&
              page.body.map((paragraph, i) => (
                <p className="mb-4" key={i}>
                  {paragraph}
                </p>
              ))}

            <p className="small text-muted mb-4">
              The information on this page is for general informational purposes about the service and does not
              constitute individualized legal advice or a guarantee of any case outcome.
            </p>

            {page.ctaPrompt && <p className="fw-semibold text-navy mb-3">{page.ctaPrompt}</p>}

            <div className="d-flex flex-wrap gap-3 mb-5">
              <Link to="/contact" className="btn btn-navy">
                Contact Us
              </Link>
              <a className="btn btn-outline-navy" href={PHONE_TEL_URL} dir="ltr">
                Call the Office
              </a>
              <a className="btn btn-success" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                Chat on WhatsApp
              </a>
            </div>

            {relatedPages.length > 0 && (
              <div className="mb-5">
                <h2 className="h6 fw-semibold text-navy mb-3">Related services</h2>
                <div className="d-flex flex-wrap gap-2">
                  {relatedPages.map((related) => (
                    <Link key={related.slug} to={related.path} className="btn btn-sm btn-outline-secondary">
                      {related.h1}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="border-top pt-4 small text-muted">
              <div className="fw-semibold text-navy mb-1">Law Office</div>
              <div>Al-Boulevard - Opposite Tripoli Serail, near the Bar Association - 3rd Floor, Tripoli, Lebanon</div>
              <div className="mt-1">
                <a className="link-navy text-decoration-none" href={PHONE_TEL_URL} dir="ltr">
                  Phone: {PHONE_DISPLAY}
                </a>
              </div>
              <a className="link-navy text-decoration-none" href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer">
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default ExpatEnPage;
